/**
 * condition-summary.ts — compact one-line text summary of stored conditions.
 * Used in list columns where full builder is not shown.
 *
 * Handles both formats:
 *   probe_result: { field: { $op: val } } or { $and/$or: [...] }
 *   data_item:    [{ key, operator, value }]
 */

const OP_SYMBOLS: Record<string, string> = {
  $eq: '=', $neq: '≠', $gt: '>', $gte: '≥', $lt: '<', $lte: '≤',
  $in: 'in', $nin: '!in', $contains: '~', $prefix: '^', $sigma: 'σ≥', $sigma_abs: 'σ±', $sigma_drop: 'σ−',
};

function opSym(op: string) { return OP_SYMBOLS[op] ?? op; }

function fmtVal(v: unknown): string {
  if (Array.isArray(v)) return `[${(v as unknown[]).slice(0, 3).join(', ')}${(v as unknown[]).length > 3 ? '…' : ''}]`;
  return String(v ?? '');
}

function mongoClauseText(obj: Record<string, unknown>): string {
  const keys = Object.keys(obj);
  if (keys.length !== 1 || keys[0].startsWith('$')) return '';
  const field = keys[0];
  const opMap = obj[field] as Record<string, unknown>;
  if (!opMap || typeof opMap !== 'object') return field;
  const op = Object.keys(opMap)[0] ?? '';
  return `${field} ${opSym(op)} ${fmtVal(opMap[op])}`;
}

/** Returns a compact human summary for display in table cells. */
export function conditionSummary(conditions: unknown): string {
  if (!conditions) return '—';

  // Array form (data_item)
  if (Array.isArray(conditions)) {
    const items = conditions as Record<string, unknown>[];
    const parts = items.slice(0, 3).map(
      (c) => `${c.key} ${opSym(String(c.operator))} ${fmtVal(c.value)}`,
    );
    const suffix = items.length > 3 ? ` +${items.length - 3} more` : '';
    return parts.join(' AND ') + suffix;
  }

  // Mongo object form (probe_result)
  if (typeof conditions === 'object' && conditions !== null) {
    const obj = conditions as Record<string, unknown>;
    const keys = Object.keys(obj);

    if (keys.length === 1 && (keys[0] === '$and' || keys[0] === '$or')) {
      const joiner = keys[0] === '$and' ? ' AND ' : ' OR ';
      const clauses = (obj[keys[0]] as unknown[]) ?? [];
      const parts = (clauses as Record<string, unknown>[])
        .slice(0, 3)
        .map(mongoClauseText)
        .filter(Boolean);
      const suffix = clauses.length > 3 ? ` +${clauses.length - 3} more` : '';
      return parts.join(joiner) + suffix;
    }

    const text = mongoClauseText(obj);
    return text || JSON.stringify(conditions).slice(0, 60);
  }

  return String(conditions).slice(0, 60);
}
